/**
 * **手柄：布局 + 按键映射 + 边沿触发的意图。**
 *
 * 标准 Gamepad 映射按**位置**编号：`0` 下 / `1` 右 / `2` 左 / `3` 上。
 * 两种布局的差别是 **AB 对调 + XY 对调**（字母印在哪个位置），不是“确认/取消语义”
 * 层面的差别。所以功能绑定的是**字母**：A=确认、B=返回、X=结束回合、Y=记牌器；
 * 字母落在哪个物理键随布局变。
 *
 * 纯函数（`gamepadIntents`）可测；DOM 轮询在 `GamepadPoller`。
 */

export type GamepadLayout = 'nintendo' | 'xbox';

export const GAMEPAD_LAYOUTS: readonly GamepadLayout[] = ['nintendo', 'xbox'];

export function parseGamepadLayout(value: unknown): GamepadLayout | null {
  return value === 'nintendo' || value === 'xbox' ? value : null;
}

export interface GamepadButtons {
  /** A 键（确认） */
  readonly confirm: number;
  /** B 键（返回） */
  readonly back: number;
  /** X 键 */
  readonly endTurn: number;
  /** Y 键 */
  readonly tracker: number;
  /** Start */
  readonly menu: number;
}

/**
 * 布局 → 各功能绑到哪个按键号（标准映射）。
 *
 * ⚠️ **AB 对调 + XY 对调**（按“字母印在哪个位置”），不是确认/取消语义的差别。
 *
 * | 字母 | XBox 位置 | 任天堂位置 |
 * | --- | --- | --- |
 * | A | 下（0） | 右（1） |
 * | B | 右（1） | 下（0） |
 * | X | 左（2） | 上（3） |
 * | Y | 上（3） | 左（2） |
 */
export function gamepadButtons(layout: GamepadLayout): GamepadButtons {
  const face =
    layout === 'nintendo'
      ? { a: 1, b: 0, x: 3, y: 2 }
      : { a: 0, b: 1, x: 2, y: 3 };
  return { confirm: face.a, back: face.b, endTurn: face.x, tracker: face.y, menu: 9 };
}

export type GamepadIntent =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'confirm'
  | 'back'
  | 'menu'
  | 'tracker'
  | 'endTurn';

/** 十字键（标准映射） */
const DPAD: readonly (readonly [number, GamepadIntent])[] = [
  [12, 'up'],
  [13, 'down'],
  [14, 'left'],
  [15, 'right'],
];

/**
 * **只报“这一帧刚按下”的意图**（边沿触发）。按住不放不会连续触发 ——
 * 焦点每帧乱跳和按钮连发都是这么来的。
 */
export function gamepadIntents(
  layout: GamepadLayout,
  pressed: readonly boolean[],
  previous: readonly boolean[],
): GamepadIntent[] {
  const map = gamepadButtons(layout);
  const edge = (index: number): boolean => (pressed[index] ?? false) && !(previous[index] ?? false);
  const out: GamepadIntent[] = [];
  for (const [index, direction] of DPAD) if (edge(index)) out.push(direction);
  if (edge(map.confirm)) out.push('confirm');
  if (edge(map.back)) out.push('back');
  if (edge(map.menu)) out.push('menu');
  if (edge(map.tracker)) out.push('tracker');
  if (edge(map.endTurn)) out.push('endTurn');
  return out;
}

export interface GamepadPoller {
  /** 这一帧的意图（可能是空的） */
  poll(): GamepadIntent[];
}

/** 轮询第一个连着的手柄，做边沿检测 */
export function createGamepadPoller(layout: () => GamepadLayout): GamepadPoller {
  let previous: readonly boolean[] = [];
  return {
    poll(): GamepadIntent[] {
      if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return [];
      const pad = Array.from(navigator.getGamepads()).find(
        (candidate): candidate is Gamepad => candidate !== null && candidate.connected,
      );
      const pressed = pad === undefined ? [] : pad.buttons.map((button) => button.pressed);
      const intents = gamepadIntents(layout(), pressed, previous);
      previous = pressed;
      return intents;
    },
  };
}
